import React from "react";
import {
  ListBuilder,
  Table,
  Format,
  ActionBar,
  ActionMenu,
} from "@redriver/cinnamon";
import { Button } from "semantic-ui-react";
import { useHistory } from "react-router-dom";
import { getUserGroups, removeUserGroup } from "./actions";
import { Sort } from "constants/enums";
import { SortHeader, RunOnChange } from "components/misc";
import { MaplesLabel } from "components/containers";
import { TablePagination } from "components/pagination";
import FilterGroups from "features/Groups/ListGroups/FilterGroups";
import AddGroupToUser from "./AddGroupToUser";
import { Actions, Targets } from "constants/permissions";
import { PermissionCheck } from "components/auth";
import RemoveGroupFromUser from "./RemoveGroupFromUser";
import BulkActionsModal from "components/modals/BulkActionsModal";
import { GroupRoutes } from "constants/routes";
import { TableActionMenu } from "components/misc";

const UserGroupsList = ({ userId }) => {
  const history = useHistory();
  return (
    <ListBuilder
      withTable
      loadAction={getUserGroups}
      loadParams={userId}
      initialFilters={{ sortName: "name", sortDirection: Sort.Asc }}
      initialPagination={{ pageSize: 25, pageNumber: 1 }}
      dataTransform={(data) => data.results}
      totalTransform={(data) => data.totalResults}
      renderList={(tableProps, state, events) => {
        const totalSelected = state.selection.allSelected
          ? state.total - (state.selection.deselected?.length || 0)
          : state.selection.selected?.length || 0;
        return (
          <div>
            <RunOnChange
              value={state.total}
              func={() => events.onChangeSelection({})}
            />
            <ActionBar>
              <ActionBar.Item>
                <FilterGroups
                  value={state.filters}
                  onChange={events.onChangeFilters}
                />
              </ActionBar.Item>
              <ActionBar.Item fluid align="right">
                <ActionMenu
                  openOnHover={false}
                  disabled={totalSelected == 0}
                  trigger={<Button compact> Actions ({totalSelected}) </Button>}
                >
                  <BulkActionsModal
                    actionName="remove"
                    header="Remove"
                    submitParams={{
                      selected: state.selection.selected,
                      deselected: state.selection.deselected,
                      allSelected: state.selection.allSelected,
                      search: state.filters.search,
                      userId: userId,
                    }}
                    onRefresh={events.onRefresh}
                    submitAction={removeUserGroup}
                    totalSelected={totalSelected}
                    as={ActionMenu.Item}
                    entityName="group"
                  >
                    Remove
                  </BulkActionsModal>
                </ActionMenu>
                <PermissionCheck action={Actions.Create} target={Targets.Group}>
                  <AddGroupToUser
                    compact
                    onAdded={events.onRefresh}
                    userId={userId}
                  />
                </PermissionCheck>
              </ActionBar.Item>
            </ActionBar>
            <Table
              {...tableProps}
              dataKey="id"
              emptyMessage="No groups found"
              selectable
              onRowClick={(group) => history.push(GroupRoutes.Group(group.id))}
            >
              <Table.SelectableColumn
                value={state.selection}
                onChange={events.onChangeSelection}
                totalItems={state.total}
              />
              <Table.Column
                header={
                  <SortHeader
                    title="Name"
                    field="name"
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                field="name"
              />
              <Table.Column
                header={
                  <SortHeader
                    title="Status"
                    field="status"
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                render={(group) =>
                  group.isActive ? (
                    <MaplesLabel type="primary">Active</MaplesLabel>
                  ) : (
                    <MaplesLabel type="default">Inactive</MaplesLabel>
                  )
                }
              />

              <Table.Column
                header={
                  <SortHeader
                    title="Sites"
                    field="sites"
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                field="sitesCount"
              />
              <Table.Column
                header={
                  <SortHeader
                    title="Users"
                    field="users"
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                field="userCount"
              />
              <Table.Column
                header={
                  <SortHeader
                    title="Created"
                    field="created"
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                render={(group) => (
                  <Format.DateTime
                    value={group.createdUtc}
                    format="DD MMM YYYY HH:mm"
                  />
                )}
              />
              <Table.Column
                header={
                  <SortHeader
                    title="Modified"
                    field="modified"
                    value={state.filters}
                    onChange={events.onChangeFilters}
                  />
                }
                render={(group) =>
                  group.modifiedUtc ? (
                    <Format.DateTime
                      value={group.modifiedUtc}
                      format="DD MMM YYYY HH:mm"
                    />
                  ) : (
                    "-"
                  )
                }
              />
              <Table.Column
                width="5%"
                align="right"
                render={(group) => (
                  <TableActionMenu>
                    <Table.MenuItem
                      onClick={() => history.push(GroupRoutes.Group(group.id))}
                    >
                      Go To Group
                    </Table.MenuItem>
                    <RemoveGroupFromUser
                      userId={userId}
                      groupId={group.id}
                      groupName={group.name}
                      onUpdated={events.onRefresh}
                    />
                  </TableActionMenu>
                )}
              />
            </Table>
            <TablePagination
              value={state.pagination}
              onChange={events.onChangePagination}
              totalItems={state.total}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>
        );
      }}
    />
  );
};

export default UserGroupsList;
